import { useState } from 'react';

import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import StandardPage from 'src/app/components/Elements/Page/Standard';
import type { CreateAddressRequestBody } from 'src/app/hooks/data/deliveryAddress';
import { useDeliveryAddressCreate } from 'src/app/hooks/data/deliveryAddress';
import { useAccountPageAddressData } from 'src/app/hooks/feature_data/useAccountPageAddressData';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { accountPage, AccountPageAddAddress } from 'src/app/router/paths';
import { closeModal, openModal } from 'src/app/state/actions';
import type { DeliveryAddress } from 'src/app/types';

import Spinner from '../../Elements/Spinner';

import { ValidateAddress } from '.';

type RecomendationState = 'recommended' | 'userEntered';

const formatAddress = (deliveryAddress?: DeliveryAddress) => {
  if (!deliveryAddress) return;
  return {
    street: deliveryAddress.address,
    street_two: deliveryAddress.addressTwo,
    city: deliveryAddress.city,
    state: deliveryAddress.state,
    zip_code: deliveryAddress.zipCode,
    is_doorman: deliveryAddress.isDoorman,
    notes: deliveryAddress.instructions,
  };
};

/*
 * This is a new version of the ValidateAddress component, which removes redux dependencies
 * and uses react-query data hooks to persist required data between components. This is meant
 * to fully replace the container/renderer setup. For now, we will use the new implementation
 * in the Account flow, with the checkout flow still using the old setup. We should migrate
 * the checkout flow to the new implementation in the future. ~ Igor, 2024-12-04
 * https://capsulerx.atlassian.net/jira/software/projects/CF/issues/CF-2914
 */
export const ValidateAddressV2 = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const [selectedAddress, setSelectedAddress] = useState<RecomendationState>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const dispatchOpenModal = ({ modalType, handleConfirmClick, confirmMessage }) =>
    dispatch(openModal({ modalType, handleConfirmClick, confirmMessage }));
  const dispatchCloseModal = () => dispatch(closeModal());

  const dispatchAddDeliveryAddressEvent = useSegmentEvent('addDeliveryAddress', { shouldTrackOnMount: false });
  const dispatchEditDeliveryAddressEvent = useSegmentEvent('editDeliveryAddress', { shouldTrackOnMount: false });

  const { addressData, setAddressData } = useAccountPageAddressData();
  const { suggestedAddress, invalidAddress } = addressData || {};
  const { mutateAsync: createDeliveryAddressAsync } = useDeliveryAddressCreate();
  const successAction = async () => {
    await setAddressData({});
    /*
     * When implementing in checkout flow:
     * - track the location conditionally
     * - do not open the modal
     * - adjust the redirect path
     */
    dispatchOpenModal({
      modalType: 'ok',
      confirmMessage: 'Great! Your delivery address has been uploaded.',
      handleConfirmClick: () => dispatchCloseModal(),
    });
    dispatchAddDeliveryAddressEvent({ location: 'Your Account' });
    history.push(accountPage.toPath());
  };

  const handleSubmitAddress = async () => {
    /*
     * Currently, this validate page only comes up in the flow for creating
     * a new address. If we implement this in an update flow, we will need
     * to adjust this.
     */
    setIsSubmitting(true);
    const deliveryAddress = selectedAddress === 'recommended' ? suggestedAddress : invalidAddress;
    if (!!deliveryAddress) {
      const payload = { ...deliveryAddress, forceCreation: true } as CreateAddressRequestBody;
      await createDeliveryAddressAsync(payload);
      successAction();
    }
    setIsSubmitting(false);
  };

  const handleEditAddress = async () => {
    await setAddressData({ existingAddress: addressData?.invalidAddress });
    dispatchEditDeliveryAddressEvent({
      zipcode: addressData?.invalidAddress?.zipCode || '',
    });
    history.push(AccountPageAddAddress.toPath());
  };

  return (
    <StandardPage>
      {isSubmitting || (!invalidAddress && !suggestedAddress) ? (
        <Spinner centered />
      ) : (
        <ValidateAddress
          invalidAddress={formatAddress(invalidAddress)}
          suggestedAddress={formatAddress(suggestedAddress)}
          handleEditClick={handleEditAddress}
          submitAddress={handleSubmitAddress}
          setSelectedAddress={setSelectedAddress}
          selectedAddress={selectedAddress}
          isFromAccountDeliveryAddress={true}
        />
      )}
    </StandardPage>
  );
};
